import { inject, Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, RouterStateSnapshot } from "@angular/router";
import { mergeMap, Observable, of } from "rxjs";
import { NoteService } from "@viewer/core/toc-items/note.service";
import { ConfirmDialogComponent } from "@viewer/shared-module/confirm-dialog/confirm-dialog.component";
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { MessageService } from "@orion2/message-service/message.service";
import { DeactivateGuardEnum } from "@viewer/toc-items/guards/deactivateGuardEnum";
import { CommentService } from "@viewer/toc-items/comments-module/services/comment.service";
import { CommentLocation } from "@orion2/models/tocitem.models";
import { TitleService } from "@viewer/header/title.service";

export interface CanComponentDeactivate {
  canDeactivate: () => Observable<boolean> | Promise<boolean> | boolean;
}

@Injectable()
export class TocItemCanDeactivateGuard {
  private readonly titleService = inject(TitleService);

  constructor(
    public dialog: MatDialog,
    private noteService: NoteService,
    private commentService: CommentService,
    private translate: TranslateService,
    private messageService: MessageService
  ) {}

  canDeactivate(
    _component: CanComponentDeactivate,
    _route: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot
  ): Observable<boolean> {
    if (this.noteService.canDeactivate() && this.commentService.canDeactivate()) {
      return of(true);
    }

    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = false;
    dialogConfig.autoFocus = true;
    dialogConfig.data = {
      content: this.translate.instant("notes.confirmDiag.text"),
      unsaveText: this.translate.instant("tocItem.doNotSave"),
      cancelText: this.translate.instant("cancel"),
      saveText: this.translate.instant("tocItem.save")
    };

    const dialogRef = this.dialog.open(ConfirmDialogComponent, dialogConfig);
    return dialogRef.afterClosed().pipe(
      mergeMap(async (response: string) => {
        switch (response) {
          case DeactivateGuardEnum.save_all: {
            const allSaved = await this.saveAll();
            return allSaved;
          }
          case DeactivateGuardEnum.not_save: {
            await this.saveNothing();
            return true;
          }
          case DeactivateGuardEnum.cancel: {
            this.cancelComment();
            return false;
          }
          default:
            return false;
        }
      })
    );
  }

  /**
   * Checks if everything was successful by checking an array of results;
   * then displays the correct message according to the situation (success, warning).
   * Only for notes and comments.
   *
   * @param results An array of booleans.
   * @param type If we are working with notes or comments.
   * @returns A boolean; `true` if every results if true, `false` otherwise.
   */
  private manageMessage(results: boolean[], type: "notes" | "comments"): boolean {
    if (!results.length) {
      return true;
    }
    const allResOk = results.every(Boolean);
    if (allResOk) {
      const message = this.translate.instant(`${type}.saveAll`);
      this.messageService.success(message);
    } else {
      const message = this.translate.instant(`${type}.canNotBeSave`);
      this.messageService.warning(message);
    }
    return allResOk;
  }

  /**
   * Saves all notes and comments before leaving the current page.
   *
   * @returns {Promise<boolean>} `true` if everything was correctly saved,
   * `false` otherwise.
   */
  private async saveAll(): Promise<boolean> {
    const saveAllNotesProms = this.noteService
      .saveAllNotes()
      .then((results: boolean[]) => this.manageMessage(results, "notes"));
    const saveAllCommentsProms = this.commentService
      .saveAll()
      .then((results: boolean[]) => this.manageMessage(results, "comments"));

    try {
      const saves = await Promise.all([saveAllNotesProms, saveAllCommentsProms]);
      const res = saves.every(Boolean);
      if (!res) {
        this.cancelComment();
      }
      return res;
    } catch {
      const message = this.translate.instant("tocItem.notSavedMsg");
      this.messageService.error(message);
      return false;
    }
  }

  /**
   * Erases all notes and comments that were not saved.
   */
  private async saveNothing(): Promise<void> {
    const deleteNotesResult = this.noteService.deleteNewNotes();
    const deleteCommentsResult = this.commentService.cancelAll();

    await Promise.all([deleteNotesResult, deleteCommentsResult]);

    const message = this.translate.instant("tocItem.notSavedMsg");
    this.messageService.success(message);
  }

  /**
   * Cancels the latest comment currently in creation if:
   * - it is not on a DM;
   * - we are not on the comments page;
   * - it was not edited.
   * This is useful if we are creating APP/PUB/INFOSET comments from a DM, but
   * we decided to cancel.
   * If we don't do that, a comment will be created without us knowing.
   */
  private cancelComment(): void {
    const latestComment = this.commentService.allComments()[0];
    if (
      latestComment.location !== CommentLocation.DM &&
      this.titleService.headerTitle !== "comments.label" &&
      !latestComment.title
    ) {
      this.commentService.cancelInitComment();
    }
  }
}
